// `shortlist.external-source-configuration.service.ng1.ts:66` — which external candidate
// sources this whitelabel has configured (plan §7 phase 3).
//
// The shortlist offers the tenant's own ATS plus any of these, and the slim header's source
// dropdown is built from the two together.
import { request } from './request.ts'

/** `SocialHostType` — the VALUES are what the server sends, not the enum key names. */
export const SocialHost = {
  Linkedin: 'Linkedin',
  Indeed: 'Indeed',
  Ats: 'ATS',
  CvLibrary: 'CVLibrary',
  Monster: 'Monster',
  Reed: 'Reed',
} as const
export type SocialHost = (typeof SocialHost)[keyof typeof SocialHost]

/** `ExternalSourceCriteriaFilterFieldType` / `...ConditionType`. String values, on the wire. */
export const ExternalSourceCriteriaFilterFieldType = {
  Text: 'TEXT',
  Numeric: 'NUMERIC',
  Date: 'DATE',
  MultiSelect: 'MULTI_SELECT',
  SingleSelect: 'SINGLE_SELECT',
} as const
export type ExternalSourceCriteriaFilterFieldType =
  (typeof ExternalSourceCriteriaFilterFieldType)[keyof typeof ExternalSourceCriteriaFilterFieldType]

export const ExternalSourceCriteriaFilterConditionType = {
  IncludeAny: 'INCLUDE_ANY',
  IncludeAll: 'INCLUDE_ALL',
  Exclude: 'EXCLUDE',
} as const
export type ExternalSourceCriteriaFilterConditionType =
  (typeof ExternalSourceCriteriaFilterConditionType)[keyof typeof ExternalSourceCriteriaFilterConditionType]

/** `IExternalSourceExtensionCriteriaFilterSupportedValue`. */
export interface ExternalSourceSupportedValue {
  value: string
  displayName: string
}

/**
 * `ICriteriaFilterConfiguration` — one filterable field of one source.
 *
 * ⚠️ `supportedConditionTypes` IS PER FIELD, not per source and not per tenant. The criteria
 * builder's condition select offers exactly these, and the first is the default for a new
 * filter — which is why this array's ORDER is part of the contract.
 */
export interface ExternalSourceCriteriaFilter {
  displayName: string
  fieldName: string
  fieldType: ExternalSourceCriteriaFilterFieldType
  supportedConditionTypes: ExternalSourceCriteriaFilterConditionType[]
  supportedValues: ExternalSourceSupportedValue[]
}

export interface ExternalSourceConfiguration {
  hostSite: SocialHost
  configured: boolean
  /** Absent on a source with no filterable fields. */
  criteriaFilters?: ExternalSourceCriteriaFilter[]
}

export interface ExternalSourceConfigurationResponse {
  configurations: ExternalSourceConfiguration[]
}

/**
 * ⚠️ `configurations` IS ALREADY THE CONFIGURED SET, despite each entry also carrying a
 * `configured` boolean. The dropdown maps the array straight to rows
 * (`shortlist-extension-alerts.component.ng1.ts:349`) without consulting that field, and the
 * template's own guard is `configurations.length > 0`. Filtering on `configured` here would
 * be a second, different rule invented by the port — if the server ever returns unconfigured
 * entries, upstream shows them too, and that is a question for upstream.
 */
export async function getExternalSourceConfiguration(): Promise<ExternalSourceConfigurationResponse> {
  return request<ExternalSourceConfigurationResponse>(
    'GET',
    '/chrome-extension-api/external-source/configuration/getConfiguration',
  )
}

/** `IExternalSourceCandidateData` — the row shape the external list answers with. */
export interface ExternalSourceCandidate {
  id: string
  name?: string
  lastPosition?: string
  lastCompany?: string
  location?: string
  viewed: boolean
  exposed: boolean
  addedToAts: boolean
  cvDate?: number
  email?: string
  phone?: string
}

export interface ExternalSourceCandidateList {
  candidates: ExternalSourceCandidate[]
  maxResults: number
}

/**
 * `/external-source/candidate-search/getCandidateDetailsByAlertId` — the list behind a source
 * OTHER than the tenant's own ATS.
 *
 * ⚠️ A DIFFERENT ENDPOINT, A DIFFERENT DTO AND A DIFFERENT ID TYPE. The candidates are the
 * SOURCE's, not Woo's: `id` is a string external id, there is no `algorithm`, and `viewed` /
 * `exposed` / `addedToAts` replace the alert flags. Reusing `getCandidatesByAlertId` with an
 * extra parameter — the obvious guess — returns the ATS list under the source's name.
 *
 * ⚠️ AND IT PAGES SERVER-SIDE, from 0. The ATS list is sliced in the client (CLAUDE.md); this
 * one takes `page` and answers one page at a time.
 */
export async function getExternalSourceCandidates(params: {
  source: string
  alertId: number
  page: number
  filtersAndSort?: unknown
  persistFilterAndSort?: boolean
}): Promise<ExternalSourceCandidateList> {
  return request('POST', '/chrome-extension-api/external-source/candidate-search/getCandidateDetailsByAlertId', params)
}
